import type { AchievementDef } from "../services/AchievementService";

const TOAST_LIFETIME_MS = 3200;
const TOAST_MAX_VISIBLE = 3;

interface Toast {
  el: HTMLElement;
  removeAt: number;
}

// Slide-in achievement chips that stack in the top-right while a run is
// active. AchievementService.onUnlocked drives `enqueue`; we render up
// to TOAST_MAX_VISIBLE simultaneously and queue the rest so a flurry of
// unlocks during a great run still gets seen.
//
// Layout:
//   <div id="hud-toasts">
//     <div class="toast tier-{bronze|silver|gold}">
//       <div class="title">UNLOCKED</div>
//       <div class="name">{achievement.title}</div>
//       <div class="desc">{achievement.description}</div>
//     </div>
//   </div>
//
// CSS handles the slide-in / pulse / slide-out animation so the JS side
// is just "add the element, remove it when its timer expires".
export class AchievementToast {
  private root: HTMLElement;
  private active: Toast[] = [];
  private queue: AchievementDef[] = [];

  constructor(root?: HTMLElement) {
    const r = root ?? (document.getElementById("hud-toasts") as HTMLElement | null);
    if (!r) throw new Error("AchievementToast: #hud-toasts not found");
    this.root = r;
  }

  // Public entrypoint. The toast appears immediately if there's room
  // (active.length < MAX_VISIBLE); otherwise it joins the back of the
  // queue and shows when an active toast retires.
  enqueue(def: AchievementDef): void {
    if (this.active.length < TOAST_MAX_VISIBLE) {
      this.show(def);
    } else {
      this.queue.push(def);
    }
  }

  // Tick from MorphHexGame.update. Cheap O(active) iteration; nothing
  // to do when no toasts are visible.
  update(): void {
    if (this.active.length === 0) return;
    const now = performance.now();
    for (let i = this.active.length - 1; i >= 0; i--) {
      const toast = this.active[i]!;
      if (now < toast.removeAt) continue;
      toast.el.classList.add("out");
      // Allow CSS exit animation to finish before fully removing.
      const el = toast.el;
      window.setTimeout(() => el.remove(), 320);
      this.active.splice(i, 1);
      // Promote queued toast to keep the stack populated.
      const next = this.queue.shift();
      if (next) this.show(next);
    }
  }

  clear(): void {
    for (const t of this.active) t.el.remove();
    this.active.length = 0;
    this.queue.length = 0;
  }

  private show(def: AchievementDef): void {
    const el = document.createElement("div");
    el.className = `toast tier-${def.tier}`;
    el.innerHTML = `
      <div class="title">UNLOCKED</div>
      <div class="name"></div>
      <div class="desc"></div>
    `;
    (el.querySelector(".name") as HTMLElement).textContent = def.title;
    (el.querySelector(".desc") as HTMLElement).textContent = def.description;
    this.root.appendChild(el);
    // Re-trigger the slide-in animation just in case the DOM batched.
    void el.offsetWidth;
    el.classList.add("in");
    this.active.push({ el, removeAt: performance.now() + TOAST_LIFETIME_MS });
  }
}
